import { MouseEventHandler } from 'react';
import { formatPhoneNumberIntl } from 'react-phone-number-input';

import {
  faBirthdayCake,
  faEnvelope,
  faMountain,
  faPhone,
  faUserCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Typography } from '@mui/joy';

import PaymentAccount from '../../Profile/PaymentAccount';
import PersonalLink from '../../Profile/PersonalLink';
import SocialAccount from '../../Profile/SocialAccount';
import { Profile } from '../../types/profile';
import Row from '../Row';
import { ReactComponent as AndWhiteIcon } from '../../assets/and-white-icon.svg';
import moment from 'moment';

export default function ShareableCard({
  andTag,
  profile,
  handleActionButtonClick,
}: {
  andTag: string;
  profile: Profile;
  handleActionButtonClick: MouseEventHandler<HTMLButtonElement>;
}) {
  const isUnclaimed = profile.personId === 'unclaimed-id';

  const AndeeAvatar = () => {
    if (!profile.profilePicUrl) {
      return (
        <FontAwesomeIcon
          icon={faUserCircle}
          color="grey"
          width={50}
          className="profile__img"
        />
      );
    }
    return (
      <img
        src={profile.profilePicUrl}
        alt={andTag}
        width={50}
        className="profile__img"
      />
    );
  };

  return (
    <div className="profile__container">
      <div className="profile__header">
        <Row className="profile__header-row">
          <div className="profile__avatar">
            <AndeeAvatar />
            <span className="profile__pronouns"> {profile.pronouns} </span>
          </div>
          <div className="profile__header">
            <h2 className="profile__tag"> {andTag} </h2>
            <h1 className="profile__name">
              {`${profile.firstName} ${profile.lastName}`}
            </h1>
            <p className="profile__professional-title">
              {profile.professionalTitle}
            </p>
            <Button
              startDecorator={<AndWhiteIcon width={'0.7rem'} />}
              size="sm"
              sx={{ fontWeight: 500, borderRadius: 10 }}
              onClick={handleActionButtonClick}
            >
              {' '}
              {profile?.personId === 'unclaimed-id'
                ? `Claim ${andTag}`
                : 'Connect'}
            </Button>
          </div>
        </Row>
        <Row flexWrap="wrap" className="profile__header-socials">
          {profile.socialAccounts &&
            Object.values(profile.socialAccounts).map((socialAccount: any) => (
              <SocialAccount
                key={socialAccount.socialAccountUrl}
                socialAccount={socialAccount}
              />
            ))}
          {profile.paymentAccounts &&
            Object.values(profile.paymentAccounts).map(
              (paymentAccount: any) => (
                <PaymentAccount
                  key={paymentAccount.paymentAccountUrl}
                  paymentAccount={paymentAccount}
                />
              )
            )}
        </Row>
      </div>

      <Row
        className="profile__content"
        flexDirection="column"
        style={{ backgroundColor: 'var(--and-palette-neutral-50)' }}
      >
        <Row>
          <Typography level="body2">
            <FontAwesomeIcon icon={faMountain} style={{ paddingRight: 5 }} />
            PURPOSE
          </Typography>
        </Row>
        <Row flexDirection="column" gap={0}>
          {profile.purposeStatement && <p>{profile.purposeStatement} </p>}
          <div style={{ lineHeight: 1.5, color: 'gray' }}>
            {profile.emailAddress && (
              <p>
                <FontAwesomeIcon
                  icon={faEnvelope}
                  style={{ marginRight: 15 }}
                />
                {profile.emailAddress}
              </p>
            )}
            {profile.phoneNumber && (
              <p>
                <FontAwesomeIcon icon={faPhone} style={{ marginRight: 15 }} />
                {formatPhoneNumberIntl(profile.phoneNumber)}
              </p>
            )}
            {!!profile.dob && (
              <p>
                <FontAwesomeIcon
                  icon={faBirthdayCake}
                  style={{ marginRight: 15 }}
                />
                {timestampToDate(profile.dob)}{' '}
              </p>
            )}
          </div>
        </Row>
      </Row>
      <Row className="profile__content" flexDirection="column">
        {Object.values(profile.personalLinks).map((personalLink, index) => (
          <PersonalLink key={index} personalLink={personalLink} />
        ))}
      </Row>
      <Row alignItems="center" justifyContent="center" flexDirection="column">
        <Button
          startDecorator={<AndWhiteIcon width={'1rem'} />}
          sx={{ borderRadius: 10 }}
          onClick={handleActionButtonClick}
        >
          {isUnclaimed
            ? `Claim ${andTag}`
            : `Connect with ${profile.firstName}`}
        </Button>
        {/* href={`/${tag}?a=co`}
                                        text={`Connect with ${firstName}`}
                                    /> */}
        <small>
          {isUnclaimed ? (
            `${andTag} could be yours`
          ) : (
            <>
              {`Are you ${profile.firstName}?`}
              <a
                href={`https://now.andme.org/${andTag}?a=mg`}
                target="_blank"
                rel="noreferrer"
              >
                {' '}
                {'Manage your &'}{' '}
              </a>
            </>
          )}
        </small>
      </Row>
    </div>
  );
}

function timestampToDate(timestamp) {
  return moment.utc(timestamp).format('MMMM Do');
}
